import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { TabMenu } from '../components/TabMenu';
import { TabMenuItem } from '../components/TabMenuItem';
import { Button } from '../components/Button';
import { Spinner } from '../components/Spinner';
import { SRErrorNotice } from '../components/SRErrorNotice';
import { SRFailedIngestModal } from '../components/SRFailedIngestModal';
import { TriageCategoryBadge } from '../components/TriageCategoryBadge';
import { ReferralDetailModal } from '../components/ReferralDetailModal';
import {
  ApiService,
  SRReferral,
  SRConnectionStatus,
  Referral,
  mapSrReferralToReferral,
} from '../services/apiService';
import { SRErrorDisplay, toSrErrorDisplay } from '../utils/srErrors';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { SrUrgencyBadge } from '../components/sr/SrUrgencyBadge';
import FeatherIcon from 'feather-icons-react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type SRTab = 'ai_triaging' | 'new' | 'assigned' | 'awaiting_reply' | 'ready_for_indici' | 'cancelled' | 'SR_declined';

interface TabCounts {
  ai_triaging: number;
  new: number;
  assigned: number;
  awaiting_reply: number;
  ready_for_indici: number;
  cancelled: number;
  SR_declined: number;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatSubscribedAt(isoString: string | null): string {
  if (!isoString) return 'Not subscribed';
  return new Date(isoString).toLocaleDateString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatCreatedAt(isoString: string): string {
  const date = new Date(isoString);
  return date.toLocaleDateString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

interface SRBadgeProps {
  referralNumber: string;
}

const SRBadge: React.FC<SRBadgeProps> = ({ referralNumber }) => (
  <span
    style={{
      display: 'inline-block',
      color: 'var(--color-text-muted, #6b7280)',
      fontSize: '0.75rem',
      fontWeight: 400,
      whiteSpace: 'nowrap',
    }}
  >
    {referralNumber}
  </span>
);

const OnforwardBadge: React.FC<{ label: string }> = ({ label }) => (
  <span
    title={label}
    style={{
      display: 'inline-block',
      backgroundColor: '#d97706',
      color: '#ffffff',
      fontSize: '0.7rem',
      fontWeight: 600,
      padding: '2px 7px',
      borderRadius: '12px',
      letterSpacing: '0.01em',
      whiteSpace: 'nowrap',
      marginTop: '4px',
    }}
  >
    Onforward
  </span>
);

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export const SRQueuePage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const highlightedReferralId = searchParams.get('referralId');
  const { user: currentUser } = useCurrentUser();
  const userRole = currentUser?.role?.toLowerCase() || '';
  const deepLinkOpenedRef = useRef(false);

  const [activeTab, setActiveTab] = useState<SRTab>('ai_triaging');
  const [referrals, setReferrals] = useState<SRReferral[]>([]);
  const [paginationMeta, setPaginationMeta] = useState<{
    current_page: number;
    total_pages: number;
    has_next: boolean;
    has_previous: boolean;
    total_count: number;
  } | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(25);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<SRErrorDisplay | null>(null);
  const [summaryError, setSummaryError] = useState<SRErrorDisplay | null>(null);

  const [tabCounts, setTabCounts] = useState<TabCounts>({
    ai_triaging: 0,
    new: 0,
    assigned: 0,
    awaiting_reply: 0,
    ready_for_indici: 0,
    cancelled: 0,
    SR_declined: 0,
  });
  const [connection, setConnection] = useState<SRConnectionStatus | null>(null);
  const [failedIngestCount, setFailedIngestCount] = useState(0);
  const [failuresModalOpen, setFailuresModalOpen] = useState(false);
  const [selectedReferral, setSelectedReferral] = useState<Referral | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // Map tab to API query parameters
  const getTabParams = (tab: SRTab): { status?: string; triageApproved?: boolean } => {
    switch (tab) {
      case 'ai_triaging':   return { triageApproved: false };
      case 'new':           return { status: 'new' };
      case 'assigned':      return { status: 'assigned' };
      case 'awaiting_reply': return { status: 'awaiting_reply' };
      case 'ready_for_indici': return { status: 'ready_for_indici' };
      case 'cancelled':     return { status: 'cancelled' };
      case 'SR_declined':   return { status: 'SR_declined' };
    }
  };

  // Fetch referrals for the active tab
  const fetchReferrals = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { status, triageApproved } = getTabParams(activeTab);
      const data = await ApiService.getSRReferrals(currentPage, pageSize, status, triageApproved);
      setReferrals(data.referrals);
      setPaginationMeta(data.pagination);
    } catch (err) {
      setError(
        toSrErrorDisplay(err, {
          code: 'SR-020',
          message: 'Unable to load SR referrals. Please try again.',
        }),
      );
      setReferrals([]);
    } finally {
      setLoading(false);
    }
  }, [activeTab, currentPage, pageSize]);

  // Fetch summary (tab counts + last synced)
  const fetchSummary = useCallback(async () => {
    try {
      const summary = await ApiService.getSRReferralsSummary();
      let cancelledCount = 0;
      let srDeclinedCount = 0;
      try {
        const cancelledData = await ApiService.getSRReferrals(1, 1, 'cancelled');
        cancelledCount = cancelledData.pagination.total_count;
      } catch (err) {
        // Cancellation history should not prevent the rest of the queue from loading.
        console.warn('Unable to load cancelled SR referral count:', err);
      }
      try {
        const declinedData = await ApiService.getSRReferrals(1, 1, 'SR_declined');
        srDeclinedCount = declinedData.pagination.total_count;
      } catch (err) {
        console.warn('Unable to load SR_declined referral count:', err);
      }
      setTabCounts({
        ...summary.tab_counts,
        cancelled: cancelledCount,
        SR_declined: srDeclinedCount,
      });
      setConnection(summary.connection);
      setFailedIngestCount(summary.failed_ingest_count ?? 0);
      setSummaryError(null);
    } catch (err) {
      setSummaryError(
        toSrErrorDisplay(err, {
          code: 'SR-030',
          message: 'Unable to load SR queue summary.',
        }),
      );
    }
  }, []);

  useEffect(() => {
    fetchReferrals();
  }, [fetchReferrals]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const handleTabChange = (tab: SRTab) => {
    setActiveTab(tab);
    setCurrentPage(1);
  };

  const handleViewDetail = async (referralId: string) => {
    try {
      const cachedSr = referrals.find((r) => r.id === referralId);
      if (cachedSr) {
        setSelectedReferral(mapSrReferralToReferral(cachedSr));
        setShowDetailModal(true);
        (async () => {
          try {
            const fullReferral = await ApiService.getReferralById(referralId);
            setSelectedReferral((prev) => (prev?.id === referralId ? fullReferral : prev));
          } catch (err) {
            console.error('Error fetching full SR referral details:', err);
          }
        })();
      } else {
        const referral = await ApiService.getReferralById(referralId);
        setSelectedReferral(referral);
        setShowDetailModal(true);
      }
    } catch (err) {
      console.error('Error opening SR referral detail:', err);
    }
  };

  const handleReferralUpdate = (updatedReferral: Referral) => {
    setSelectedReferral(updatedReferral);
    fetchReferrals();
    fetchSummary();
  };

  // Deep-link: ?referralId= opens detail modal once
  useEffect(() => {
    if (!highlightedReferralId || deepLinkOpenedRef.current) {
      return;
    }
    deepLinkOpenedRef.current = true;
    handleViewDetail(highlightedReferralId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlightedReferralId]);

  const isConnected = Boolean(connection?.connected && connection.subscription_active);

  const columnCount = 7;

  return (
    <div style={{ padding: '0 0 24px' }}>

        {/* ---- Header ---- */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px',
          }}
        >
          <div>
            <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 600 }}>
              SR Referrals
            </h2>
            <div
              style={{
                marginTop: '4px',
                fontSize: '0.85rem',
                color: 'var(--color-text-muted, #6b7280)',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <FeatherIcon icon={connection?.connected ? 'link' : 'link-2'} size={13} />
              <span>
                SR: {connection?.connected && connection.subscription_active
                  ? `Connected (user ${connection.sr_user_id})`
                  : connection?.connected
                    ? 'Connected — subscription inactive'
                    : 'Not connected'}
              </span>
              {connection?.subscribed_at && (
                <span>· Subscribed {formatSubscribedAt(connection.subscribed_at)}</span>
              )}
            </div>
            <Link to="/admin/sr-integration" className="admin-inline-link">
              Manage SR connection
            </Link>
          </div>
          <div>
            <Button variant="secondary" onClick={() => setFailuresModalOpen(true)}>
              Failed ingestions
              {failedIngestCount > 0 && (
                <span
                  style={{
                    marginLeft: '8px',
                    display: 'inline-block',
                    minWidth: '20px',
                    padding: '0 6px',
                    borderRadius: '10px',
                    backgroundColor: '#dc2626',
                    color: '#fff',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    textAlign: 'center',
                  }}
                >
                  {failedIngestCount}
                </span>
              )}
            </Button>
          </div>
        </div>

        <SRFailedIngestModal
          isOpen={failuresModalOpen}
          onClose={() => setFailuresModalOpen(false)}
        />

        {!isConnected && (
          <div className="admin-notice admin-notice--warning" style={{ marginBottom: '12px' }}>
            SR is not connected. Configure{' '}
            <Link to="/admin/sr-integration">SR Integration</Link> before referrals will be received.
          </div>
        )}

        {summaryError && (
          <div style={{ marginBottom: '12px' }}>
            <SRErrorNotice
              code={summaryError.code}
              message={summaryError.message}
              variant="warning"
            />
          </div>
        )}

        {error && (
          <div style={{ marginBottom: '12px' }}>
            <SRErrorNotice code={error.code} message={error.message} />
          </div>
        )}

        {/* ---- Tabs ---- */}
        <TabMenu variant="queue">
          <TabMenuItem
            id="ai_triaging"
            label="AI Triaging"
            count={tabCounts.ai_triaging}
            isActive={activeTab === 'ai_triaging'}
            onClick={() => handleTabChange('ai_triaging')}
          />
          <TabMenuItem
            id="new"
            label="New"
            count={tabCounts.new}
            isActive={activeTab === 'new'}
            onClick={() => handleTabChange('new')}
          />
          <TabMenuItem
            id="assigned"
            label="Assigned"
            count={tabCounts.assigned}
            isActive={activeTab === 'assigned'}
            onClick={() => handleTabChange('assigned')}
          />
          <TabMenuItem
            id="awaiting_reply"
            label="Awaiting Reply"
            count={tabCounts.awaiting_reply}
            isActive={activeTab === 'awaiting_reply'}
            onClick={() => handleTabChange('awaiting_reply')}
          />
          <TabMenuItem
            id="ready_for_indici"
            label="Ready for Indici"
            count={tabCounts.ready_for_indici}
            isActive={activeTab === 'ready_for_indici'}
            onClick={() => handleTabChange('ready_for_indici')}
          />
          <TabMenuItem
            id="cancelled"
            label="Cancelled"
            count={tabCounts.cancelled}
            isActive={activeTab === 'cancelled'}
            onClick={() => handleTabChange('cancelled')}
          />
          <TabMenuItem
            id="SR_declined"
            label="SR Declined"
            count={tabCounts.SR_declined}
            isActive={activeTab === 'SR_declined'}
            onClick={() => handleTabChange('SR_declined')}
          />
        </TabMenu>

        {/* ---- Referral table ---- */}
        <div className="referrals-table" style={{ marginTop: '12px' }}>
          <table>
            <thead>
              <tr>
                <th style={{ fontWeight: 'normal', color: 'var(--color-text-muted, #6b7280)', fontSize: '0.82rem' }}>SR #</th>
                <th style={{ fontWeight: 'normal' }}>Patient</th>
                <th style={{ fontWeight: 'normal' }}>GP / Practice</th>
                <th style={{ fontWeight: 'normal' }}>Referral Type</th>
                <th style={{ fontWeight: 'normal' }}>Triage</th>
                <th style={{ fontWeight: 'normal' }}>Status</th>
                <th style={{ fontWeight: 'normal' }}>Created</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan={columnCount}
                    style={{ textAlign: 'center', padding: '32px' }}
                  >
                    <Spinner size="sm" color="secondary" />
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td
                    colSpan={columnCount}
                    style={{ textAlign: 'center', padding: '20px', color: 'var(--color-text-muted, #6b7280)' }}
                  >
                    Unable to load referrals. See the error above.
                  </td>
                </tr>
              ) : referrals.length === 0 ? (
                <tr>
                  <td
                    colSpan={columnCount}
                    style={{
                      textAlign: 'center',
                      padding: '40px 20px',
                      color: 'var(--color-text-muted, #6b7280)',
                    }}
                  >
                    No SR referrals in this category
                  </td>
                </tr>
              ) : (
                referrals.map((referral) => (
                  <tr
                    key={referral.id}
                    onClick={() => handleViewDetail(referral.id)}
                    style={{
                      cursor: 'pointer',
                      backgroundColor:
                        highlightedReferralId === referral.id
                          ? 'rgba(37, 99, 235, 0.08)'
                          : undefined,
                    }}
                  >
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '3px' }}>
                        <SRBadge referralNumber={referral.sr_referral_number} />
                        <SrUrgencyBadge isUrgent={referral.is_urgent} />
                        {referral.referred_from_id != null && referral.on_refer_info && (
                          <OnforwardBadge label={referral.on_refer_info} />
                        )}
                      </div>
                    </td>
                    <td>
                      <span style={{ color: 'var(--axis-vibrant-orange)' }}>
                        {referral.patient_given_name} {referral.patient_family_name}
                      </span>
                    </td>
                    <td>
                      <div style={{ lineHeight: 1.4 }}>
                        {referral.referring_provider_name && (
                          <div style={{ fontWeight: 500 }}>
                            {referral.referring_provider_name}
                          </div>
                        )}
                        {referral.referring_provider_org && (
                          <div
                            style={{
                              fontSize: '0.82rem',
                              color: 'var(--color-text-muted, #6b7280)',
                            }}
                          >
                            {referral.referring_provider_org}
                          </div>
                        )}
                        {!referral.referring_provider_name && !referral.referring_provider_org && (
                          <span style={{ color: 'var(--color-text-muted, #6b7280)' }}>—</span>
                        )}
                      </div>
                    </td>
                    <td>
                      {referral.referral_type ? (
                        <span className="status-badge">{referral.referral_type}</span>
                      ) : (
                        <span style={{ color: 'var(--color-text-muted, #6b7280)' }}>—</span>
                      )}
                    </td>
                    <td>
                      {referral.triage_category_final != null ? (
                        <TriageCategoryBadge
                          category={referral.triage_category_final}
                          variant="short"
                          showApproved={referral.triage_approved}
                          title="Triage Category"
                        />
                      ) : (
                        <span style={{ color: 'var(--color-text-muted, #6b7280)' }}>—</span>
                      )}
                    </td>
                    <td>
                      <span className={`status-badge status-${referral.status}`}>
                        {referral.status.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td style={{ whiteSpace: 'nowrap', fontSize: '0.875rem' }}>
                      {formatCreatedAt(referral.created_at)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* ---- Pagination ---- */}
        {paginationMeta && paginationMeta.total_pages > 1 && (
          <div className="pagination" style={{ marginTop: '12px' }}>
            <Button
              variant="secondary"
              disabled={!paginationMeta.has_previous || loading}
              onClick={() => setCurrentPage((p) => p - 1)}
            >
              Previous
            </Button>
            <span className="pagination-info">
              Page {paginationMeta.current_page} of {paginationMeta.total_pages}
              {' '}
              <span style={{ color: 'var(--color-text-muted, #6b7280)', fontSize: '0.85rem' }}>
                ({paginationMeta.total_count} total)
              </span>
            </span>
            <Button
              variant="secondary"
              disabled={!paginationMeta.has_next || loading}
              onClick={() => setCurrentPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        )}
        <ReferralDetailModal
          referral={selectedReferral}
          isOpen={showDetailModal}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedReferral(null);
            deepLinkOpenedRef.current = false;
            if (highlightedReferralId) {
              const next = new URLSearchParams(searchParams);
              next.delete('referralId');
              setSearchParams(next, { replace: true });
            }
            fetchReferrals();
            fetchSummary();
          }}
          onReferralUpdate={handleReferralUpdate}
          userRole={userRole}
        />
    </div>
  );
};
